// @ts-strict-ignore
import { AppStoreBuild, StabilityIssue } from '../../types';
import * as React from 'react';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import List from 'antd/lib/list';
import StabilityMetrics from './StabilityMetrics';
import { antGrey, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import StabilityIssues from '../stability/StabilityIssues';
import Space from 'antd/lib/space';
import { nonFatalStabilityIssuesSupportedForIntegration, stabilityIssuesSupportedForIntegration } from '../../helpers';
import AppStoreBuildComponent from '../appStoreBuilds/AppStoreBuildComponent';
import StabilityIssueDrawer from '../stability/StabilityIssueDrawer';
import { selectVCSIntegration, selectStabilityMonitoringIntegration } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import Alert from 'antd/lib/alert';

interface StabilityMetricsProps {
    appId: string;
    minWidth?: number | string;
    maxWidth?: number;
    titleStyle?: React.CSSProperties;
    backgroundColor: string;
    isRunwayAutoPaused?: boolean;
    builds: AppStoreBuild[];
    shouldHideIntegrationTitle?: boolean;
}

const StabilityMetricsContainer: React.FC<StabilityMetricsProps> = (props: StabilityMetricsProps) => {
    const hasBuilds = props.builds.length > 0;
    const integrationId = hasBuilds ? props.builds[0].stabilityInfo.integrationId : null; // assumes all builds have the same integrationId
    const [icon, alt] = iconAndAltForIntegrationType(integrationId);

    const stabilityIntegrationError = useSelector((state: RootState) => {
        const stabIntegration = selectStabilityMonitoringIntegration(state, props.appId);

        return !!stabIntegration?.integrationError;
    });

    const baseTitleStyle: React.CSSProperties = { ...titleStyle, color: antGrey.grey7, fontSize: 12 };

    const count = props.builds.length;

    const environment = hasBuilds ? props.builds[0].stabilityInfo?.environmentName : null; // assumes all builds are associated with same environment
    const stabTitleStyle = props.titleStyle ?? baseTitleStyle;

    const shouldShowIntegrationTitle = !props.shouldHideIntegrationTitle;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 20 }}>
            {stabilityIntegrationError && (
                <Alert
                    type="warning"
                    message={`There's an error with your stability monitoring integration. Runway may not reflect the most up to date data.`}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                {shouldShowIntegrationTitle && (
                    <>
                        {icon && (
                            <div>
                                <img src={icon} alt={alt} width={30} height={30} />
                            </div>
                        )}
                        <Text style={stabTitleStyle}>
                            Stability
                            {environment && (
                                <Text
                                    style={{
                                        ...stabTitleStyle,
                                        color: antGrey.grey6,
                                        textTransform: 'capitalize',
                                        fontWeight: 300,
                                    }}
                                >
                                    {' '}
                                    - {environment}
                                </Text>
                            )}
                        </Text>
                    </>
                )}
                {count === 1 && props.builds[0].stabilityInfo.url && (
                    <div style={{ marginLeft: 'auto' }}>
                        <ViewInStabilityPlatformButton build={props.builds[0]} />
                    </div>
                )}
            </div>
            <StabilityMetricsContainerBase {...props} />
        </div>
    );
};

export const StabilityMetricsContainerBase: React.FC<StabilityMetricsProps> = (props: StabilityMetricsProps) => {
    const [showDrawerForIssue, setShowDrawerForIssue] = React.useState<StabilityIssue>(null);
    const hasBuilds = props.builds.length > 0;
    const integrationId = hasBuilds ? props.builds[0].stabilityInfo.integrationId : null; // assumes all builds have the same integrationId
    const showBuildInfo = props.builds.length > 1;

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));

    const count = props.builds.length;

    return (
        <>
            <List
                split
                style={{ flexGrow: 1, minWidth: 0 }}
                itemLayout="vertical"
                dataSource={props.builds}
                renderItem={(build, index) => {
                    const isFirst = index === 0;
                    const isLast = index === count - 1;

                    return (
                        <List.Item key={index} style={{ paddingTop: isFirst ? 0 : 16, paddingBottom: isLast ? 0 : 16 }}>
                            <Space direction="vertical" style={{ alignItems: 'stretch', width: '100%' }} size={16}>
                                {showBuildInfo && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div>{<AppStoreBuildComponent {...build} />}</div>
                                        {build.stabilityInfo.url && <ViewInStabilityPlatformButton build={build} />}
                                    </div>
                                )}
                                <StabilityMetrics
                                    {...build.stabilityInfo}
                                    isRunwayAutoPaused={props.isRunwayAutoPaused}
                                    backgroundColor={props.backgroundColor}
                                />
                                {stabilityIssuesSupportedForIntegration(integrationId) && (
                                    <StabilityIssues
                                        topNewStabilityIssues={build.stabilityInfo.topNewStabilityIssues}
                                        topStabilityIssues={build.stabilityInfo.topStabilityIssues}
                                        topNewStabilityIssuesNonFatal={
                                            build.stabilityInfo.topNewStabilityIssuesNonFatal
                                        }
                                        topStabilityIssuesNonFatal={build.stabilityInfo.topStabilityIssuesNonFatal}
                                        backgroundColor={props.backgroundColor}
                                        shouldShowNonFatals={nonFatalStabilityIssuesSupportedForIntegration(
                                            integrationId,
                                        )}
                                        onShowDrawerForIssue={(issue) => setShowDrawerForIssue(issue)}
                                    />
                                )}
                            </Space>
                        </List.Item>
                    );
                }}
                locale={{ emptyText: 'No stability data found' }}
            ></List>
            <StabilityIssueDrawer
                open={!!showDrawerForIssue}
                integrationId={integrationId}
                issue={showDrawerForIssue}
                onClose={() => setShowDrawerForIssue(null)}
                vcsId={vcsIntegration?.id}
                appId={props.appId}
            />
        </>
    );
};

export const ViewInStabilityPlatformButton = ({ build }: { build: AppStoreBuild }) => {
    return (
        <Button type="link" href={build.stabilityInfo.url} target="_blank" style={{ padding: 0 }}>
            View in {displayNameForIntegrationType(build.stabilityInfo.integrationId)}
        </Button>
    );
};

export default StabilityMetricsContainer;
